import axios, { CancelTokenSource } from 'axios';
import DeleteActionButton from 'components/DeleteActionButton';
import EditActionButton from 'components/EditActionButton';
import { DropdownOption, mapToDropdownOptionArray } from 'components/final-form/dropdownUtils';
import Input from 'components/final-form/Input';
import Select from 'components/final-form/Select';
import PraxStyledSegment from 'components/layout/PraxStyledSegment';
import PxGrid from 'components/layout/PxGrid';
import StyledActionButton from 'components/layout/StyledActionButton';
import StyledViewTableRow from 'components/layout/StyledViewTableRow';
import VirtualizedTable from 'components/layout/VirtualizedTable';
import SearchResultComponent from 'components/SearchResultComponent';
import UpsertPrintoutConfigForm from 'components/settings/printout-config/UpsertPrintoutConfigForm';
import { FormState } from 'final-form';
import React, { Component } from 'react';
import { Field, Form as FinalForm, FormSpy } from 'react-final-form';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Dimmer, Grid, Icon, Loader, Popup } from 'semantic-ui-react';
import { getAllMandates } from 'services/mandateServices';
import {
  deletePrintoutConfig,
  findPrintoutConfigById,
  getAllPrinters,
  getPrintoutsForDefaultMandate,
  getPrintoutsForMandate
} from 'services/printoutConfigServices';
import { debounce } from 'ts-debounce';
import { PrintOrientation, ReportKey } from 'ts-types/api.enums';
import {
  PrinterConfigDto,
  PrintoutConfigDto,
  PrintoutSearchRequest,
  UpsertPrintoutConfigDto
} from 'ts-types/api.types';
import { noop } from 'utils/functionUtils';

const PopupStyle = {
  borderRadius: 5,
  opacity: 0.7,
  padding: '7px'
};

interface Props extends MessageSourceProps {

}

interface State {
  printoutConfigId?: number,
  printoutConfig?: Partial<UpsertPrintoutConfigDto>,
  printers: Array<PrinterConfigDto>,
  printersMap: Map<number, PrinterConfigDto>,
  reportKeyName?: string,
  reportKeys: Array<string>,
  mandates: Array<DropdownOption>,
  printoutSearchRequest: Partial<PrintoutSearchRequest>,
  foundPrintoutConfigs: Array<Partial<PrintoutConfigDto>>,
  loadingPrintoutConfigs: boolean,
  upsertPrintoutConfigModalOpen: boolean,
  cancelTokenSource: CancelTokenSource,
}

const initialFormValues: Partial<PrintoutSearchRequest> = {
  mandateId: -1,
  searchKey: undefined
}

class PrintoutConfigView extends Component<Props, State> {

  private mounted: boolean = false;

  constructor(props: Props) {
    super(props);

    const {getMessage} = props;
    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      mandates: [],
      printoutSearchRequest: {
        mandateId: -1,
        searchKey: ""
      },
      reportKeys: Object.values(ReportKey),
      foundPrintoutConfigs: [],
      printers: [],
      printersMap: new Map<number, PrinterConfigDto>(),
      loadingPrintoutConfigs: true,
      upsertPrintoutConfigModalOpen: false,
      cancelTokenSource: cancelTokenSource
    };

    const onFinally = () => {
      this.setState({
        loadingPrintoutConfigs: false
      })
    };

    getAllMandates(cancelTokenSource)
        .then(response => {

          const defaultMandate =
              {
                key: -1,
                text: getMessage("default"),
                value: -1
              };

          this.setState({
            mandates: [defaultMandate, ...mapToDropdownOptionArray(response, "name", "id")]
          });
        }).catch(noop)
        .finally(onFinally);

    getAllPrinters(cancelTokenSource)
        .then(response => {

          let printersMap = new Map<number, PrinterConfigDto>();
          response.forEach(printer => {
            printersMap.set(printer.id, printer);
          })
          this.setState({
            printers: response,
            printersMap: printersMap
          })
        }).catch(noop)
        .finally(onFinally);
  }

  componentDidMount(): void {
    this.mounted = true;
  }

  componentWillUnmount(): void {
    this.mounted = false;

    const {cancelTokenSource} = this.state;
    cancelTokenSource.cancel('Operation canceled by user');
  }

  initialPrintoutConfigValues = (reportKey: string): Partial<UpsertPrintoutConfigDto> => {

    const {printoutSearchRequest} = this.state;

    return {
      mandateId: printoutSearchRequest && printoutSearchRequest.mandateId ? printoutSearchRequest.mandateId : -1,
      description: "default",
      reportKey: reportKey,
      orientation: PrintOrientation.PORTRAIT,
      preview: false,
      printDialog: false,
      template: "",
      header: "",
      footer: ""
    };
  };

  openUpsertPrintoutConfigModal = (reportKey: string, id?: number) => {

    const {foundPrintoutConfigs, cancelTokenSource} = this.state;

    const foundReportKey = foundPrintoutConfigs.find(reportKey => reportKey === reportKey);

    if (foundReportKey) {
      const reportKeyName = foundReportKey.reportKeyName;
      this.setState({
        reportKeyName: reportKeyName
      });
    }

    if (id) {
      findPrintoutConfigById(id, cancelTokenSource).then(response => {
        this.showModal(id, response);
      }).catch(noop);
    } else {
      this.showModal(undefined, this.initialPrintoutConfigValues(reportKey));
    }
  };

  showModal = (id?: number, printoutConfig?: Partial<UpsertPrintoutConfigDto>) => {

    this.setState(({}: State) => {
      return {
        upsertPrintoutConfigModalOpen: true,
        printoutConfigId: id,
        printoutConfig: printoutConfig
      }
    });
  };

  hideModal = () => {

    this.setState(({}: State) => ({
      upsertPrintoutConfigModalOpen: false,
      printoutConfigId: undefined,
      printoutConfig: undefined
    }));
  };

  onUpsertPrintoutConfig = (printoutConfigDto: PrintoutConfigDto) => {

    const {foundPrintoutConfigs: printoutConfigs} = this.state;

    const foundPrintoutConfigsIndex = printoutConfigs
        .findIndex(printoutConfig => printoutConfig.id == printoutConfigDto.id);

    if (foundPrintoutConfigsIndex != -1) {
      const modifiedPrintoutConfigs = Array.from(printoutConfigs);

      // @ts-ignore
      printoutConfigDto.reportKeyName = modifiedPrintoutConfigs[foundPrintoutConfigsIndex].reportKeyName;

      modifiedPrintoutConfigs[foundPrintoutConfigsIndex] = {
        ...printoutConfigDto
      };

      this.setState({
        foundPrintoutConfigs: modifiedPrintoutConfigs
      })
    } else {

      const foundPrintoutConfigKeyIndex = printoutConfigs
          .findIndex(printoutConfig => printoutConfig.reportKey == printoutConfigDto.reportKey);

      const modifiedPrintoutConfigs = Array.from(printoutConfigs);

      // @ts-ignore
      printoutConfigDto.reportKeyName = modifiedPrintoutConfigs[foundPrintoutConfigKeyIndex].reportKeyName;

      modifiedPrintoutConfigs[foundPrintoutConfigKeyIndex] = {
        ...printoutConfigDto
      };

      this.setState({
        foundPrintoutConfigs: modifiedPrintoutConfigs
      });
    }

    this.hideModal();
  };

  handlePrintoutConfigsSearchChange = debounce(({values}: FormState) => {

    const {mandateId, searchKey} = values;

    if (!this.mounted) {
      return;
    }

    const {cancelTokenSource} = this.state;

    const searchRequest = {
      mandateId: mandateId,
      searchKey: searchKey
    }

    this.setState({
      loadingPrintoutConfigs: true,
      printoutSearchRequest: searchRequest
    });

    const onFinally = () => {
      this.setState({
        loadingPrintoutConfigs: false
      })
    };

    if (mandateId && mandateId >= 0) {
      getPrintoutsForMandate(searchRequest, cancelTokenSource)
          .then(response => {
            this.setState({
              foundPrintoutConfigs: response
            })
          })
          .catch(noop)
          .finally(onFinally);
    } else {
      const defaultSearchRequest = {
        mandateId: undefined,
        searchKey: searchRequest.searchKey
      }
      getPrintoutsForDefaultMandate(defaultSearchRequest, cancelTokenSource)
          .then(response => {
            this.setState({
              foundPrintoutConfigs: response
            })
          })
          .catch(noop)
          .finally(onFinally);
    }

  }, 300);

  onDeletePrintoutConfig = (id: number) => {

    const {foundPrintoutConfigs, cancelTokenSource} = this.state;

    deletePrintoutConfig(id, cancelTokenSource)
        .then(() => {

          const modifiedPrintoutConfigs: Array<Partial<PrintoutConfigDto>> = foundPrintoutConfigs
              .map(printoutConfig => {
                if (id === printoutConfig.id) {
                  return {
                    reportKey: printoutConfig.reportKey,
                    reportKeyName: printoutConfig.reportKeyName
                  }
                }

                return printoutConfig;
              });

          this.setState({
            foundPrintoutConfigs: modifiedPrintoutConfigs
          })
        }).catch(noop);
  };

  renderFinalForm = () => {
    const {printoutSearchRequest} = this.state;
    return (printoutSearchRequest &&
        <FinalForm
            onSubmit={noop}
            initialValues={initialFormValues}
            subscription={{pristine: true}}
            render={this.renderSearchForm}
        />
    )
  }

  render(): React.ReactNode {

    const {
      printoutSearchRequest,
      printoutConfig,
      reportKeyName,
      mandates,
      printoutConfigId,
      loadingPrintoutConfigs,
      upsertPrintoutConfigModalOpen,
      printers
    } = this.state;

    const mandate = mandates.find(m => m.value === printoutSearchRequest.mandateId);

    return (
        <PraxStyledSegment>
          {this.renderFinalForm()}
          <div>
            {
                upsertPrintoutConfigModalOpen &&
                <UpsertPrintoutConfigForm
                    modalOpen={upsertPrintoutConfigModalOpen}
                    onCloseModal={this.hideModal}
                    onSave={this.onUpsertPrintoutConfig}
                    printoutConfigId={printoutConfigId}
                    printoutConfig={printoutConfig}
                    printers={printers}
                    reportKeyName={reportKeyName}
                    mandate={mandate}
                />
            }
          </div>

          <StyledViewTableRow>
            {loadingPrintoutConfigs ? this.renderLoader() : this.renderPrintoutConfigsSegment()}
          </StyledViewTableRow>

        </PraxStyledSegment>
    );
  }

  renderSearchForm = (): React.ReactNode => {

    const {getMessage} = this.props;
    const {mandates, foundPrintoutConfigs} = this.state;

    const numResults = foundPrintoutConfigs.length;

    return (
        <PxGrid style={{marginBottom: 25}}>
          <Grid.Row>
            <Grid.Column width={3} verticalAlign="middle">
              {getMessage("mandate")}:
            </Grid.Column>
            <Grid.Column width={4}>
              <Field
                  name="mandateId"
                  component={Select}
                  fluid
                  options={mandates}
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={3} verticalAlign="middle">
              {getMessage("Report Key")}:
            </Grid.Column>
            <Grid.Column width={4}>
              <Field
                  name="searchKey"
                  component={Input}
                  fluid
              />
            </Grid.Column>
          </Grid.Row>
          <SearchResultComponent numResults={numResults} textMessage={"search.resultsSummary"}/>
          <FormSpy subscription={{values: true, pristine: true}} onChange={this.handlePrintoutConfigsSearchChange}/>
        </PxGrid>
    )
  };

  rowGetter = ({index}: any) => {
    const {foundPrintoutConfigs} = this.state;

    Object.assign(foundPrintoutConfigs[index], {index: index + 1});

    return foundPrintoutConfigs[index];
  };

  foundPrintoutConfigsRowRenderer = ({className, columns, index, key, style, rowData}: any) => {

    const {id, reportKey} = rowData;
    const ariaProps = {'aria-rowindex': index + 1};

    return (
        <div
            {...ariaProps}
            className={className}
            key={key}
            role="row"
            style={style}
            onDoubleClick={() => this.openUpsertPrintoutConfigModal(reportKey, id)}
        >
          {columns}
        </div>
    );
  };
  reportKeyNameCell = ({rowData}: any) => {
    const {reportKeys} = this.state;
    const {getMessage} = this.props;
    const {reportKeyName} = rowData;

    if (reportKeys.includes(reportKeyName)) {
      return getMessage(`reportKey.${reportKeyName}`)
    }

    return reportKeyName;
  };

  printerCellRender = ({cellData}: any) => {
    const {printersMap} = this.state;

    const printer = printersMap.get(cellData);

    if (printer) {
      return printer.name;
    }
    return "";

  };

  previewCell = ({rowData}: any) => {
    const {getMessage} = this.props;
    const {id, preview} = rowData;

    if (id !== null && preview) {
      return getMessage("printoutConfig.preview");
    }
  };

  printDialogCell = ({rowData}: any) => {
    const {getMessage} = this.props;
    const {id, printDialog} = rowData;

    if (id !== null && printDialog) {
      return getMessage("printoutConfig.printDialog");
    }
  };

  orientationCell = ({rowData}: any) => {
    const {getMessage} = this.props;
    const {id, orientation} = rowData;

    if (id !== null && orientation) {
      return getMessage(`orientation.${orientation}`);
    }
  };

  actionsCell = ({rowData}: any) => {
    const {getMessage} = this.props;
    const {id, reportKey} = rowData;
    if (id) {
      return (
          <React.Fragment>
            <EditActionButton
                hoverMessage={getMessage('printoutConfig.edit')}
                onConfirm={() => this.openUpsertPrintoutConfigModal(reportKey, id)}
            />
            <DeleteActionButton
                hoverMessage={getMessage('printoutConfig.delete')}
                popupMessage={getMessage("printoutConfig.confirmDelete")}
                onConfirm={() => this.onDeletePrintoutConfig(id)}
            />
          </React.Fragment>
      );
    }

    return (
        <StyledActionButton icon onClick={() => this.openUpsertPrintoutConfigModal(reportKey)}>
          <Popup
              trigger={<Icon name={'plus'}/>}
              content={getMessage('printoutConfig.add')}
              size="small"
              position="top center"
              style={PopupStyle}
              inverted
          />
        </StyledActionButton>
    );

  };

  renderPrintoutConfigsSegment = (): React.ReactNode => {

    const {foundPrintoutConfigs} = this.state;
    const {getMessage} = this.props;

    return (
        <VirtualizedTable
            rowCount={foundPrintoutConfigs.length}
            rowGetter={this.rowGetter}
            rowRenderer={this.foundPrintoutConfigsRowRenderer}
            rowHeight={42}
            columns={[
              {
                width: 100,
                label: (getMessage("id")),
                dataKey: "id"
              },
              {
                width: 400,
                flexGrow: 1,
                label: (getMessage("printoutConfig.reportKey")),
                dataKey: "reportKeyName",
                cellRenderer: (this.reportKeyNameCell)
              },
              {
                width: 400,
                flexGrow: 1,
                label: (getMessage("printoutConfig.printer")),
                dataKey: "printerConfId",
                cellRenderer: (this.printerCellRender)
              },
              {
                width: 250,
                label: (getMessage("printoutConfig.preview")),
                dataKey: "preview",
                cellRenderer: (this.previewCell)
              },
              {
                width: 250,
                label: (getMessage("printoutConfig.printDialog")),
                dataKey: "printDialog",
                cellRenderer: (this.printDialogCell)
              },
              {
                width: 300,
                label: (getMessage("printoutConfig.orientation")),
                dataKey: "orientation",
                cellRenderer: (this.orientationCell)
              },
              {
                width: 100,
                label: (getMessage("actions")),
                dataKey: "id",
                cellRenderer: (this.actionsCell)
              }
            ]}
        />
    )
  };

  renderLoader = (): React.ReactNode => {

    const {getMessage} = this.props;

    return (
        <Dimmer active inverted>
          <Loader inline="centered" size="medium">{getMessage("printoutConfig.loader")}</Loader>
        </Dimmer>
    );
  };

}

export default withMessages(PrintoutConfigView);
